import { FC, useEffect, useMemo } from 'react'
import config from '@/config.ts'
import { DataGrid } from '@/presentation/common/components/dataGrid/DataGrid.tsx'
import {
  DataGridApiConfig,
  DataGridColumnDefs,
  DataGridDefaultColDef,
} from '@/presentation/common/components/dataGrid/lib/api.types.ts'
import { Maintainer } from '@/presentation/common/components/maintainer/Maintainer.tsx'
import { MTCellAvatarV2 } from '@/presentation/common/components/maintainer/table/cells/MTCellAvatarV2.tsx'
import { MTCellUidV2 } from '@/presentation/common/components/maintainer/table/cells/MTCellUidV2.tsx'
import { stringComparator } from '@/presentation/common/components/table/comparators/stringComparator.ts'
import { useAlert } from '@/presentation/common/hooks/useAlert.ts'
import { useMQ } from '@/presentation/common/hooks/useMediaQuery.ts'
import { useMaintainerStore } from '@/presentation/common/store/zustand/maintainer.ts'
import { TenantsActions } from '@/presentation/tenants/forms/TenantsActions.tsx'
import { useDownloadTenantsExcel } from '@/presentation/tenants/hooks/useDownloadTenantsExcel.ts'
import { useQueryTenantsForTable } from '@/presentation/tenants/hooks/useQueryTenantsForTable.ts'

const Tenants: FC = () => {
  const { isTabletAndBelow } = useMQ()
  const { reset, setTitle, setSelectionV2, setActionsSlot, search } = useMaintainerStore()
  const { data, query, refresh, isLoading } = useQueryTenantsForTable()

  const defaultColDef: DataGridDefaultColDef = useMemo(() => ({
    sortable  : true,
    resizable : true,
    minWidth  : 110,
  }), [])

  const columnDefs: DataGridColumnDefs[] = useMemo(() => ([
    {
      field                   : 'picture',
      headerName              : 'Imagen',
      width                   : 100,
      resizable               : false,
      cellRenderer            : MTCellAvatarV2,
      cellRendererParams      : { justify: 'center' },
      sortable                : false,
      formatValue             : ({ value }) => value ? `${config.publicBucketUrl}/${value}` : '',
      headerCheckboxSelection : true,
      checkboxSelection       : true,
    },
    { field: 'name', headerName: 'Nombre', comparator: stringComparator },
    { field: 'cdn', headerName: 'CDN', comparator: stringComparator },
    { field: 'contactEmail', headerName: 'Contacto', comparator: stringComparator },
    {
      field        : 'id',
      headerName   : 'ID',
      cellRenderer : MTCellUidV2,
      comparator   : stringComparator,
    },
  ]), [])

  const tableConfig = useMemo(() => ({
    useMobileView : isTabletAndBelow,
    defaultColDef,
    columnDefs,
    rowHeight     : 50,
    rowData       : data?.items || [],
    rowSelection  : 'multiple',
    pagination    : {
      enabled      : true,
      pageSize     : data?.limit || 10,
      currentPage  : data?.page || 1,
      totalItems   : data?.totalItems || 0,
      onPageChange : (page: number, limit: number) => query({
        params: {
          page,
          limit,
          search,
        },
      }),
    },
    onSelectionChange: (_selectedRow, selectedRows) => setSelectionV2(selectedRows),
  } as DataGridApiConfig), [ data, data?.items, data?.limit, data?.page, data?.totalItems, isTabletAndBelow ])

  const actionsComponent = useMemo(() => <TenantsActions onAction={refresh}/>, [ refresh ])

  // actualizar actions slot
  useEffect(() => {
    setActionsSlot(actionsComponent)
  }, [ actionsComponent ])

  // first load
  useEffect(() => {
    reset()
    setTitle('Inquilinos')
    setActionsSlot(actionsComponent)
    query({
      params: {
        page: 1,
      },
    })
  }, [])

  // when search changes update query
  useEffect(() => {
    query({
      params: {
        search,
        page: 1,
      },
    })
  }, [ search ])

  // DOWNLOAD EXCEL
  const {
    submit: downloadFile,
    isLoading: isExcelLoading,
    isError: isExcelError,
    isSuccess: isExcelSuccess,
    message: excelMessage,
    data: excelFile,
    lastUpdate: lastSubmitTimestamp,
  } = useDownloadTenantsExcel()

  const {
    showLoading: showExcelLoading,
    showError: showExcelError,
    closeAlert: closeExcelAlert,
  } = useAlert({ messageApiKey: 'TenantsExcel-Alert' })

  // Mostrar alerta de excel
  useEffect(() => {
    if (isExcelLoading) {
      showExcelLoading(excelMessage)
    }
    else if (isExcelError) {
      showExcelError(excelMessage)
    }
    else {
      closeExcelAlert()
    }
  }, [ isExcelLoading, isExcelError, isExcelSuccess, excelMessage ])

  // Abrir excel cuando se descargue
  useEffect(() => {
    if (!isExcelLoading && excelFile) {
      window.open(URL.createObjectURL(excelFile))
    }
  }, [ lastSubmitTimestamp ])

  return (
    <Maintainer showExcelDownloadButton onExcelDownloadButtonClick={downloadFile}>
      <DataGrid config={tableConfig} isLoading={isLoading}/>
    </Maintainer>
  )
}

export default Tenants
